import React, { createContext, useState, useContext, ReactNode } from 'react';
import endpoints from '../utils/commonVariables/endpoints';
import { viewModelEntities } from '../utils/commonVariables/entityKeys';
import api from '../services/api';
import { useNotification } from './NotificationsContext';
import { EntityTab } from '../types/entities/entities';
import { IHotelOwner } from '../types/entities/IHotelOwner';
import { IdHotelOwnerKey } from '../utils/userProfileContextHelpers';
import { FeedbackMessages } from '../utils/commonVariables/messages';

interface HotelOwnerContextType {
    ownerData: HotelOwnerState | null;
    getInitialOwnerData: () => Promise<IHotelOwner | undefined>;
    saveOwnerData: (data: HotelOwnerState) => void;
    updateOwnerData: (partialData: Partial<HotelOwnerState>) => void;
}

const HotelOwnerContext = createContext<HotelOwnerContextType | undefined>(
    undefined
);

export interface HotelOwnerState {
    id: string;
    name: string;
    signed: boolean;
    zinantisMembership: boolean;
    idCurrency: number;
    currency: string;
    currencyCode: string;
}

export const HotelOwnerProvider = ({ children }: { children: ReactNode }) => {
    const { setErrorMessage } = useNotification();

    const [ownerData, setOwnerData] = useState<HotelOwnerState | null>(null);

    const getInitialOwnerData = async () => {
        const ownerContractModel: EntityTab<IHotelOwner> = {
            Id: true,
            Name: true,
            Signed: true,
            ZinantisMembership: true,
            IdCurrency: true,
            Currency: {
                Name: true,
                ISO_4217: true,
            },
            SystemUser: {
                UserName: true,
            },
            Users: {
                ReadOnly: true,
                Admin: true,
                SystemUser: {
                    UserName: true,
                },
            },
            ConfigurationValues: {
                DateLastUpdated: true,
                Enabled: true,
                Id: true,
                IdAleConfigurationKey: true,
                Key: true,
                Name: true,
                Value: true,
                AleConfigurationKey: {
                    Id: true,
                    Enabled: true,
                    Description: true,
                    FieldType: {
                        IsBoolean: true,
                        IsDate: true,
                        IsNumeric: true,
                        IsString: true,
                    },
                },
            },
        };
        const IdHotelOwner = localStorage.getItem(IdHotelOwnerKey);
        const endpoint = endpoints.viewModel(
            viewModelEntities.HotelOwner,
            IdHotelOwner
        );
        try {
            const { data } = await api.post(endpoint, ownerContractModel);
            if (data?.Result) {
                return data?.Result as IHotelOwner;
            } else {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        }
    };

    const saveOwnerData = (data: HotelOwnerState) => {
        setOwnerData(data);
    };

    const updateOwnerData = (partialData: Partial<HotelOwnerState>) => {
        if (!ownerData) return;
        setOwnerData(prevData => {
            if (!prevData) return prevData;
            return { ...prevData, ...partialData } as HotelOwnerState;
        });
    };

    return (
        <HotelOwnerContext.Provider
            value={{
                ownerData,
                getInitialOwnerData,
                saveOwnerData,
                updateOwnerData,
            }}
        >
            {children}
        </HotelOwnerContext.Provider>
    );
};

export const useHotelOwner = () => {
    const context = useContext(HotelOwnerContext);
    if (!context) {
        throw new Error(
            'useHotelOwner must be used within a HotelOwnerProvider'
        );
    }
    return context;
};

export const useIdHotelOwner = () => {
    const { ownerData } = useHotelOwner();
    const idHotelOwnerLS = localStorage.getItem(IdHotelOwnerKey);

    return ownerData?.id || idHotelOwnerLS;
};

export const useHotelOwnerName = () => {
    const { ownerData } = useHotelOwner();
    return ownerData?.name;
};

export const useHotelOwnerCurrency = () => {
    const { ownerData } = useHotelOwner();
    return {
        currency: ownerData?.currency,
        idCurrency: ownerData?.idCurrency,
        currencyCode: ownerData?.currencyCode,
    };
};
